import React from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import type { CampaignType } from './CampaignBasicsStep';
import { BUDGET_CONFIG } from '@/config/constants';

export type BiddingStrategy =
  | 'MANUAL_CPC'
  | 'TARGET_CPA'
  | 'TARGET_ROAS'
  | 'TARGET_IMPRESSION_SHARE'
  | 'MAXIMIZE_CONVERSIONS'
  | 'MAXIMIZE_CLICKS'
  | 'MAXIMIZE_CONVERSION_VALUE';

interface BiddingStrategyOption {
  value: BiddingStrategy;
  label: string;
  description: string;
  supportedCampaignTypes: CampaignType[];
}

const BIDDING_STRATEGIES: BiddingStrategyOption[] = [
  {
    value: 'MANUAL_CPC',
    label: 'Manual CPC',
    description: 'Set your own maximum cost-per-click bids',
    supportedCampaignTypes: ['SEARCH', 'DISPLAY', 'SHOPPING'],
  },
  {
    value: 'MAXIMIZE_CLICKS',
    label: 'Maximize Clicks',
    description: 'Automatically set bids to get the most clicks within your budget',
    supportedCampaignTypes: ['SEARCH', 'DISPLAY', 'SHOPPING', 'DEMAND_GEN'],
  },
  {
    value: 'TARGET_CPA',
    label: 'Target CPA',
    description: 'Automatically set bids to get as many conversions at your target cost-per-action (Smart Bidding)',
    supportedCampaignTypes: ['SEARCH', 'DISPLAY', 'SHOPPING', 'PERFORMANCE_MAX', 'DEMAND_GEN'],
  },
  {
    value: 'TARGET_ROAS',
    label: 'Target ROAS',
    description: 'Automatically set bids to maximize conversion value at your target return on ad spend (Smart Bidding)',
    supportedCampaignTypes: ['SEARCH', 'DISPLAY', 'SHOPPING', 'PERFORMANCE_MAX', 'DEMAND_GEN'],
  },
  {
    value: 'TARGET_IMPRESSION_SHARE',
    label: 'Target Impression Share',
    description: 'Automatically set bids to show your ads at a target location and percentage (ideal for brand protection)',
    supportedCampaignTypes: ['SEARCH', 'DISPLAY'],
  },
  {
    value: 'MAXIMIZE_CONVERSIONS',
    label: 'Maximize Conversions',
    description: 'Automatically set bids to get the most conversions within your budget (Smart Bidding - recommended for AI Max for Search)',
    supportedCampaignTypes: ['SEARCH', 'DISPLAY', 'SHOPPING', 'PERFORMANCE_MAX', 'DEMAND_GEN'],
  },
  {
    value: 'MAXIMIZE_CONVERSION_VALUE',
    label: 'Maximize Conversion Value',
    description: 'Automatically set bids to maximize total conversion value within your budget (Smart Bidding)',
    supportedCampaignTypes: ['SEARCH', 'DISPLAY', 'SHOPPING', 'PERFORMANCE_MAX', 'DEMAND_GEN'],
  },
];

interface BudgetBiddingStepProps {
  campaignType: CampaignType;
  dailyBudget: string;
  biddingStrategy: BiddingStrategy;
  enableAiMax: boolean;
  targetCpaValue: string;
  targetRoasValue: string;
  targetImpressionShareLocation: string;
  targetImpressionShareLocationFraction: string;
  targetImpressionShareCpcCeiling: string;
  onChangeBudget: (budget: string) => void;
  onChangeBiddingStrategy: (strategy: BiddingStrategy) => void;
  onChangeEnableAiMax: (enabled: boolean) => void;
  onChangeTargetCpa: (value: string) => void;
  onChangeTargetRoas: (value: string) => void;
  onChangeTargetImpressionShareLocation: (value: string) => void;
  onChangeTargetImpressionShareLocationFraction: (value: string) => void;
  onChangeTargetImpressionShareCpcCeiling: (value: string) => void;
  errors?: {
    budget?: string;
    targetCpa?: string;
    targetRoas?: string;
    targetImpressionShareLocationFraction?: string;
    targetImpressionShareCpcCeiling?: string;
  };
}

export const BudgetBiddingStep: React.FC<BudgetBiddingStepProps> = ({
  campaignType,
  dailyBudget,
  biddingStrategy,
  enableAiMax,
  targetCpaValue,
  targetRoasValue,
  targetImpressionShareLocation,
  targetImpressionShareLocationFraction,
  targetImpressionShareCpcCeiling,
  onChangeBudget,
  onChangeBiddingStrategy,
  onChangeEnableAiMax,
  onChangeTargetCpa,
  onChangeTargetRoas,
  onChangeTargetImpressionShareLocation,
  onChangeTargetImpressionShareLocationFraction,
  onChangeTargetImpressionShareCpcCeiling,
  errors,
}) => {
  // Filter bidding strategies based on campaign type
  // Memoize to prevent creating new array reference on every render
  const availableStrategies = React.useMemo(
    () => BIDDING_STRATEGIES.filter((strategy) =>
      strategy.supportedCampaignTypes.includes(campaignType)
    ),
    [campaignType]
  );

  // Ensure selected strategy is supported by current campaign type
  React.useEffect(() => {
    const isCurrentStrategySupported = availableStrategies.some(
      (s) => s.value === biddingStrategy
    );
    if (!isCurrentStrategySupported && availableStrategies.length > 0) {
      onChangeBiddingStrategy(availableStrategies[0].value);
    }
  }, [campaignType, biddingStrategy, availableStrategies, onChangeBiddingStrategy]);

  // Determine if AI Max checkbox should be shown
  // Only show for Search campaigns with Smart Bidding strategies
  const smartBiddingStrategies: BiddingStrategy[] = [
    'TARGET_CPA',
    'TARGET_ROAS',
    'MAXIMIZE_CONVERSIONS',
    'MAXIMIZE_CONVERSION_VALUE',
  ];
  const showAiMaxCheckbox =
    campaignType === 'SEARCH' && smartBiddingStrategies.includes(biddingStrategy);

  // Auto-disable AI Max when switching away from Smart Bidding
  React.useEffect(() => {
    if (!showAiMaxCheckbox && enableAiMax) {
      onChangeEnableAiMax(false);
    }
  }, [showAiMaxCheckbox, enableAiMax, onChangeEnableAiMax]);

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid decimal numbers
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      onChangeBudget(value);
    }
  };

  const budgetValue = parseFloat(dailyBudget) || 0;

  // Compute monthly estimate label and value
  const timeSpanLabel = BUDGET_CONFIG.TIME_SPAN_LABEL.charAt(0).toUpperCase() + BUDGET_CONFIG.TIME_SPAN_LABEL.slice(1);
  const monthlyEstimate = (budgetValue * BUDGET_CONFIG.AVERAGE_DAYS_PER_STANDARD_TIME_SPAN).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Budget & Bidding</h3>
        <p className="text-sm text-gray-600">
          Set your daily budget and choose how you want to bid for ad placements.
        </p>
      </div>

      {/* Daily Budget */}
      <div>
        <label htmlFor="daily-budget" className="block text-sm font-medium text-gray-700 mb-2">
          Daily Budget <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-shrink-0" style={{ width: '200px' }}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">$</span>
            </div>
            <Input
              id="daily-budget"
              type="text"
              value={dailyBudget}
              onChange={handleBudgetChange}
              placeholder="50.00"
              className={cn('pl-7', {
                'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.budget,
              })}
              aria-invalid={!!errors?.budget}
              aria-describedby="daily-budget-description daily-budget-error"
            />
          </div>
          {budgetValue > 0 && (
            <div className="hidden sm:block flex-shrink-0">
              <p className="text-sm text-gray-700">
                {timeSpanLabel} estimate: <span className="font-semibold text-gray-900">${monthlyEstimate}</span>
              </p>
            </div>
          )}
        </div>
        {errors?.budget && (
          <p id="daily-budget-error" className="mt-1 text-sm text-red-600">
            {errors.budget}
          </p>
        )}
        <p id="daily-budget-description" className="mt-1 text-xs text-gray-500">
          The average amount you're willing to spend each day.
        </p>
        {budgetValue > 0 && (
          <p className="sm:hidden mt-1 text-sm text-gray-700">
            {timeSpanLabel} estimate: <span className="font-semibold text-gray-900">${monthlyEstimate}</span>
          </p>
        )}
      </div>

      {/* Bidding Strategy */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Bidding Strategy <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {availableStrategies.map((strategy) => {
            const isSelected = biddingStrategy === strategy.value;
            const showAiMaxInCard = isSelected && showAiMaxCheckbox && smartBiddingStrategies.includes(strategy.value);
            return (
              <div
                key={strategy.value}
                className={cn(
                  'border-2 rounded-lg transition-all',
                  {
                    'border-blue-600 bg-blue-50': isSelected,
                    'border-gray-200': !isSelected,
                  }
                )}
              >
                <label
                  className={cn(
                    'flex items-start p-4 cursor-pointer rounded-lg',
                    'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500',
                    {
                      'hover:bg-blue-50': isSelected,
                    }
                  )}
                >
                  <input
                    type="radio"
                    name="bidding-strategy"
                    value={strategy.value}
                    checked={isSelected}
                    onChange={() => onChangeBiddingStrategy(strategy.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                    aria-describedby={`strategy-${strategy.value}-description`}
                  />
                  <div className="ml-3 flex-1">
                    <span className="block text-sm font-medium text-gray-900">{strategy.label}</span>
                    <p
                      id={`strategy-${strategy.value}-description`}
                      className="text-xs text-gray-500 mt-1"
                    >
                      {strategy.description}
                    </p>
                  </div>
                </label>
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-out',
                    showAiMaxInCard ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'
                  )}
                >
                  <div className="px-4 pb-3 ml-7 mt-1">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableAiMax}
                        onChange={(e) => onChangeEnableAiMax(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                      />
                      <div className="ml-3 flex-1">
                        <span className="block text-sm font-medium text-gray-900">
                          Enable AI Max for Search
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          Uses Google's AI for keywordless targeting and optimized ad combinations
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Target CPA Input */}
      {biddingStrategy === 'TARGET_CPA' && (
        <div>
          <label htmlFor="target-cpa" className="block text-sm font-medium text-gray-700 mb-2">
            Target CPA (optional)
          </label>
          <div className="relative" style={{ width: '200px' }}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">$</span>
            </div>
            <Input
              id="target-cpa"
              type="text"
              value={targetCpaValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  onChangeTargetCpa(value);
                }
              }}
              placeholder="10.00"
              className={cn('pl-7', {
                'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.targetCpa,
              })}
              aria-invalid={!!errors?.targetCpa}
              aria-describedby="target-cpa-description target-cpa-error"
            />
          </div>
          {errors?.targetCpa && (
            <p id="target-cpa-error" className="mt-1 text-sm text-red-600">
              {errors.targetCpa}
            </p>
          )}
          <p id="target-cpa-description" className="mt-1 text-xs text-gray-500">
            The target cost you want to pay for each conversion. Leave empty to let Google optimize without a specific target.
          </p>
        </div>
      )}

      {/* Target ROAS Input */}
      {biddingStrategy === 'TARGET_ROAS' && (
        <div>
          <label htmlFor="target-roas" className="block text-sm font-medium text-gray-700 mb-2">
            Target ROAS (optional)
          </label>
          <div className="relative" style={{ width: '200px' }}>
            <Input
              id="target-roas"
              type="text"
              value={targetRoasValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  onChangeTargetRoas(value);
                }
              }}
              placeholder="4.00"
              className={cn({
                'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.targetRoas,
              })}
              aria-invalid={!!errors?.targetRoas}
              aria-describedby="target-roas-description target-roas-error"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">x</span>
            </div>
          </div>
          {errors?.targetRoas && (
            <p id="target-roas-error" className="mt-1 text-sm text-red-600">
              {errors.targetRoas}
            </p>
          )}
          <p id="target-roas-description" className="mt-1 text-xs text-gray-500">
            The target return on ad spend (e.g., 4.00 means $4 in revenue for every $1 spent). Leave empty to let Google optimize without a specific target.
          </p>
        </div>
      )}

      {/* Target Impression Share Settings */}
      {biddingStrategy === 'TARGET_IMPRESSION_SHARE' && (
        <div className="space-y-4">
          <div>
            <label htmlFor="impression-share-location" className="block text-sm font-medium text-gray-700 mb-2">
              Ad Location <span className="text-red-500">*</span>
            </label>
            <select
              id="impression-share-location"
              value={targetImpressionShareLocation}
              onChange={(e) => onChangeTargetImpressionShareLocation(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              aria-describedby="impression-share-location-description"
            >
              <option value="ANYWHERE_ON_PAGE">Anywhere on results page</option>
              <option value="TOP_OF_PAGE">Top of results page</option>
              <option value="ABSOLUTE_TOP_OF_PAGE">Absolute top of results page</option>
            </select>
            <p id="impression-share-location-description" className="mt-1 text-xs text-gray-500">
              Where you want your ads to appear on the search results page
            </p>
          </div>

          <div>
            <label htmlFor="impression-share-fraction" className="block text-sm font-medium text-gray-700 mb-2">
              Target Impression Share % <span className="text-red-500">*</span>
            </label>
            <div className="relative" style={{ width: '200px' }}>
              <Input
                id="impression-share-fraction"
                type="text"
                value={targetImpressionShareLocationFraction}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                    onChangeTargetImpressionShareLocationFraction(value);
                  }
                }}
                placeholder="75"
                className={cn({
                  'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.targetImpressionShareLocationFraction,
                })}
                aria-invalid={!!errors?.targetImpressionShareLocationFraction}
                aria-describedby="impression-share-fraction-description impression-share-fraction-error"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 text-sm">%</span>
              </div>
            </div>
            {errors?.targetImpressionShareLocationFraction && (
              <p id="impression-share-fraction-error" className="mt-1 text-sm text-red-600">
                {errors.targetImpressionShareLocationFraction}
              </p>
            )}
            <p id="impression-share-fraction-description" className="mt-1 text-xs text-gray-500">
              Target percentage of eligible impressions (1-100%)
            </p>
          </div>

          <div>
            <label htmlFor="impression-share-cpc-ceiling" className="block text-sm font-medium text-gray-700 mb-2">
              Max CPC Bid (optional)
            </label>
            <div className="relative" style={{ width: '200px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 text-sm">$</span>
              </div>
              <Input
                id="impression-share-cpc-ceiling"
                type="text"
                value={targetImpressionShareCpcCeiling}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                    onChangeTargetImpressionShareCpcCeiling(value);
                  }
                }}
                placeholder="5.00"
                className={cn('pl-7', {
                  'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.targetImpressionShareCpcCeiling,
                })}
                aria-invalid={!!errors?.targetImpressionShareCpcCeiling}
                aria-describedby="impression-share-cpc-ceiling-description impression-share-cpc-ceiling-error"
              />
            </div>
            {errors?.targetImpressionShareCpcCeiling && (
              <p id="impression-share-cpc-ceiling-error" className="mt-1 text-sm text-red-600">
                {errors.targetImpressionShareCpcCeiling}
              </p>
            )}
            <p id="impression-share-cpc-ceiling-description" className="mt-1 text-xs text-gray-500">
              Maximum cost per click to maintain target impression share
            </p>
          </div>
        </div>
      )}

      {/* Conversion Tracking Notice */}
      {['TARGET_CPA', 'TARGET_ROAS', 'MAXIMIZE_CONVERSIONS', 'MAXIMIZE_CONVERSION_VALUE'].includes(
        biddingStrategy
      ) && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <svg
              className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-yellow-800">Conversion Tracking Notice</h4>
              <p className="mt-1 text-sm text-yellow-700">
                {campaignType === 'PERFORMANCE_MAX'
                  ? 'If conversion tracking is not set up, the campaign will default to click-based targeting/optimization.'
                  : 'This bidding strategy works best with conversion tracking set up in your Google Ads account.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
